import { env } from '../env';

/**
 * A request that reached the server and came back with a failing status.
 *
 * STMN-3697 / P5. This class exists so a failure reports the STATUS rather than whatever
 * the JSON parser happened to say about the error page. That distinction is the whole
 * subject of the ticket: CVE-2026-72898 produced a visible symptom on 2026-09-12 -- broken
 * Metabase authentication, so no dashboards -- and it stayed unread for six weeks because
 * the only thing anyone saw was a generic message.
 */
export class HttpRequestError extends Error {
  readonly status: number;
  readonly method: string;
  readonly endpoint: string;

  constructor(
    status: number,
    method: string,
    endpoint: string,
    options?: ErrorOptions
  ) {
    super(`${method} ${endpoint} failed with HTTP ${status}`, options);
    this.name = 'HttpRequestError';
    this.status = status;
    this.method = method;
    this.endpoint = endpoint;
  }
}

/**
 * The address, stripped of its query string, for use in an error message.
 *
 * The query string is dropped on purpose and it is not cosmetic: embed tokens travel in
 * URLs on this platform (§7 of the incident dossier), and these messages are sent to
 * Sentry. An error meant to improve visibility must not become a new way to leak a
 * credential. The response BODY is left out for the same reason -- the status and the
 * endpoint are what was missing, and they are enough.
 */
function describeEndpoint(url: string): string {
  try {
    const parsed = new URL(url);
    return `${parsed.origin}${parsed.pathname}`;
  } catch {
    return url.split('?')[0];
  }
}

export class Service {
  URL: string;

  constructor(URL: string) {
    this.URL = URL;
  }

  async request(url: string, method: string, body = {}, headers = {}) {
    let req = await fetch(url, {
      method: method,
      credentials: 'include',
      headers: {
        ...headers,
        'content-type': 'application/json'
      },
      body: method != 'GET' ? JSON.stringify(body) : undefined
    });

    const endpoint = describeEndpoint(url);

    if (req.status == 401) {
      console.log('you are not logged in');
      location.href = env.VITE_ACCOUNT_UI_URL;
      // The redirect is kept, but it must not be the end of the story. Previously
      // execution fell through to req.json() here: an unauthenticated reply whose body is
      // not JSON then raised a SyntaxError, and THAT is what propagated -- an error about a
      // token at position 0, from a request that failed because of authentication.
      throw new HttpRequestError(req.status, method, endpoint);
    }

    if (!req.ok) {
      // Every other failing status used to fall through to the parser as well: 403, 500,
      // 502 all surfaced as parse errors or as `undefined` fields further up.
      throw new HttpRequestError(req.status, method, endpoint);
    }

    try {
      return await req.json();
    } catch (error) {
      // A broken body on a 2xx IS a parse error. Keep it as one, and keep its cause, so it
      // stays distinguishable from the failures above.
      throw new Error(`${method} ${endpoint} returned a body that is not JSON`, {
        cause: error
      });
    }
  }

  async get(url: string) {
    return await this.request(`${this.URL}${url}`, 'GET');
  }

  async post(url: string, data: any) {
    return await this.request(`${this.URL}${url}`, 'POST', data);
  }

  async put(url: string, data: any) {
    return await this.request(`${this.URL}${url}`, 'PUT', data);
  }

  async delete(url: string) {
    return await this.request(`${this.URL}${url}`, 'DELETE');
  }
}
